<template>
  <HeadlessPopover
    v-slot="{ open: isOpen, close: closePopover }"
    class="relative"
  >
    <HeadlessPopoverButton
      ref="buttonRef"
      as="template"
      :is-open.camel
      :close="closePopover"
      @focus="handleFocus"
      @mousedown="handleMousedown"
    >
      <slot name="button" :is-open :toggle></slot>
    </HeadlessPopoverButton>

    <RedesignWebccDialog
      v-if="$screen.isSmall"
      full-screen
      :open="isOpen"
      @close="close"
    >
      <slot :is-open :toggle></slot>
    </RedesignWebccDialog>

    <HeadlessPopoverPanel
      v-else
      class="z-50 bg-bgr absolute mt-2 rounded-lg border border-bgr-300 shadow-lg"
    >
      <slot :is-open :toggle></slot>
    </HeadlessPopoverPanel>
  </HeadlessPopover>
</template>

<script setup lang="ts">
import type { PopoverButton } from '@headlessui/vue'
import { isBoolean, isFunction } from 'lodash-es'

defineExpose({ toggle })

type SlotProps = {
  isOpen: boolean
  toggle(value?: boolean | undefined): void
}

defineSlots<{
  'button'(props: SlotProps): unknown
  'default'(props: SlotProps): unknown
}>()

const buttonRef = shallowRef<InstanceType<typeof PopoverButton>>()

const { focused } = useFocus(buttonRef)

const isClicking = ref(false)

function handleMousedown() {
  isClicking.value = !focused.value
}

function handleFocus() {
  isClicking.value = false
}

function close() {
  handleMousedown()
  const closeFn = buttonRef.value?.$attrs.close
  if (!isFunction(closeFn)) return
  closeFn()
}

function toggle(value?: boolean | undefined) {
  if (isClicking.value) return

  const isOpen = !!buttonRef.value?.$attrs.isOpen

  if (isBoolean(value) && value === isOpen) return

  if (isOpen) {
    close()
  } else {
    buttonRef.value?.$el.click()
  }
}
</script>
